/**
 * Home — Tile Tavern main menu.
 *
 * Three-tier responsive grid:
 *   PC (>=1100px):  identity | hero | notifs / doorways span / featured + dailies
 *   Mid (700-1099): identity+notifs left, hero right / doorways span / featured+dailies
 *   Mobile (<700):  single column with compact header, primary CTA above the fold,
 *                   featured + dailies collapse to the bottom.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Character3DThumbnail } from '../characters/Character3DThumbnail';
import { LiveCharacterAvatar } from './LiveCharacterAvatar';
import type { Character, LobbyTicket, GameTile, CharacterStats } from '../../lib/api';
import { createLobby, joinLobby, listGames, getCharacterStats, getSeasonState, setCharacterPin, getRegulars, ApiError } from '../../lib/api';
import type { RegularEntry } from '@tt/lobby/regulars';
import { PinPad } from '../characters/PinPad';
import { track } from '../../lib/analytics';
import { PublicBrowser } from './PublicBrowser';
import { XPBar } from '../me/XPBar';
import { BellButton } from '../me/BellButton';
import { DailyChallengeWidget } from '../me/DailyChallengeWidget';
import { CollapseSection } from '../me/CollapseSection';
import {
  Target,
  Sparkles,
  Trophy,
  Crown,
  Backpack,
  Settings as SettingsIcon,
  Ticket,
  HelpCircle,
  X,
  Repeat as RepeatIcon,
  Flame as FlameIcon,
  Home as HomeIcon,
} from 'lucide-react';
import { GameThumb } from './GameThumb';
import { MeDrawer } from '../me/MeDrawer';
import { BuyLanternsModal } from '../payments/BuyLanternsModal';
import { ToastQueue, useToastQueue } from '../me/UnlockToast';
import { drainPendingToasts } from '../../lib/pendingToasts';
import { featuredGameForWeek, FEATURED_XP_MULTIPLIER } from '@tt/lobby/season';
import { useEscapeClose } from '../../lib/useEscapeClose';
import { CosmeticThumbnail3D } from '../characters/CosmeticThumbnail3D';
import { inventoryToCatalogId } from '@tt/lobby/cosmetic-resolver';

interface Props {
  character: Character;
  onSwap: () => void;
  onEnterLobby: (ticket: LobbyTicket) => void;
  onOpenHof: () => void;
  onOpenAchievements: () => void;
  onOpenLocker: () => void;
  onOpenSeason: () => void;
  onOpenShop: () => void;
  /** "Your Own Tavern" Phase 2 — opens the Hall Builder editor. */
  onOpenTavern: () => void;
  onCharacterUpdated: (character: Character) => void;
  /** Phase D — re-fetch the cached Character from the server. Bound by
   *  App.tsx; called after the inline BuyLanternsModal closes so the
   *  home pip reflects a webhook-driven balance change without a reload. */
  onCharacterRefresh?: () => void | Promise<void>;
}

const SEASON_PASS_NAG_KEY_PREFIX = 'tt:seasonNag:';

const REASONS: Record<string, string> = {
  'lobby-not-found': "That room doesn't exist.",
  'lobby-full': 'That room is full.',
  'lobby-closed': 'That room already closed.',
  'bad-code': 'Codes are 4 letters (A–Z, no O/I/L).',
  'no-actor': 'Pick a character first.',
};

function prettifyGameId(id: string): string {
  return id.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
}

export function Home({
  character,
  onSwap,
  onEnterLobby,
  onOpenHof,
  onOpenAchievements,
  onOpenLocker,
  onOpenSeason,
  onOpenShop,
  onOpenTavern,
  onCharacterUpdated,
  onCharacterRefresh,
}: Props) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'create' | 'join' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [games, setGames] = useState<GameTile[]>([]);
  const [stats, setStats] = useState<CharacterStats | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  useEffect(() => {
    if (character.hasPin === false) { setPinError(null); setPinOpen(true); }
  }, [character.charId, character.hasPin]);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [meOpen, setMeOpen] = useState(false);
  const [identityMenuOpen, setIdentityMenuOpen] = useState(false);
  const [hostModalOpen, setHostModalOpen] = useState(false);
  // OWNER DIRECTIVE (2026-07-04): PIN is MANDATORY at the beginning and
  // not skippable — the modal below force-opens for any pin-less
  // character and cannot be cancelled until a PIN is set. The old
  // dismissible ribbon is retired (kept false pending NPE redesign).
  const pinMandatory = character.hasPin === false;
  // Persist visibility so the modal preselects last choice.
  const [visibility, setVisibility] = useState<'public' | 'private'>(() => {
    try { return localStorage.getItem('tt:lobbyVisibility') === 'public' ? 'public' : 'private'; }
    catch { return 'private'; }
  });
  const [browseOpen, setBrowseOpen] = useState(false);
  const [buyLanternsOpen, setBuyLanternsOpen] = useState(false);
  const homeToasts = useToastQueue();
  const [seasonUnclaimedCount, setSeasonUnclaimedCount] = useState<number>(0);
  const [seasonNagFingerprint, setSeasonNagFingerprint] = useState<string>('');
  function setVisibilityPersist(v: 'public' | 'private') {
    setVisibility(v);
    try { localStorage.setItem('tt:lobbyVisibility', v); } catch { /* */ }
  }

  useEffect(() => {
    void (async () => {
      try { setGames((await listGames()).games); } catch { /* */ }
    })();
  }, []);

  useEffect(() => {
    setStats(null);
    void (async () => {
      try { setStats(await getCharacterStats(character.charId)); } catch { /* profile flair is optional */ }
    })();
  }, [character.charId]);

  useEffect(() => {
    const queued = drainPendingToasts();
    if (queued.length === 0) return;
    for (const t of queued) {
      homeToasts.push({
        tier: t.tier ?? 2,
        rarity: t.rarity ?? 3,
        kind: t.kind ?? 'currency',
        title: t.title,
        ...(t.subtitle !== undefined ? { subtitle: t.subtitle } : {}),
        ...(t.rewardChip !== undefined ? { rewardChip: t.rewardChip } : {}),
        ...(t.icon !== undefined ? { icon: t.icon } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const s = await getSeasonState();
        if (!live) return;
        const claimedFree = new Set(s.claimedFree);
        const claimedPrem = new Set(s.claimedPremium);
        let unclaimed = 0;
        for (let tier = 1; tier <= s.tier; tier++) {
          if (!claimedFree.has(tier)) unclaimed += 1;
          if (s.hasPremium && !claimedPrem.has(tier)) unclaimed += 1;
        }
        if (live) {
          setSeasonUnclaimedCount(unclaimed);
          setSeasonNagFingerprint(`s${s.season}-t${s.tier}-u${unclaimed}`);
        }
      } catch { /* badge is decorative */ }
    })();
    return () => { live = false; };
  }, [character.charId]);

  const seasonNagDismissedKey = `${SEASON_PASS_NAG_KEY_PREFIX}${character.charId}:${seasonNagFingerprint}`;
  const seasonNagDismissed = (() => {
    if (!seasonNagFingerprint) return true;
    try { return localStorage.getItem(seasonNagDismissedKey) === '1'; } catch { return false; }
  })();
  const showSeasonNag = seasonUnclaimedCount > 0 && !seasonNagDismissed;
  function dismissSeasonNag() {
    try { localStorage.setItem(seasonNagDismissedKey, '1'); } catch { /* */ }
    setSeasonUnclaimedCount(0);
  }

  async function create(v: 'public' | 'private') {
    if (busy) return;
    setVisibilityPersist(v);
    setHostModalOpen(false);
    setBusy('create'); setError(null);
    try { onEnterLobby(await createLobby({ visibility: v })); }
    catch (e) { setError(REASONS[(e as ApiError).code] ?? 'Could not create room.'); setBusy(null); }
  }

  async function join() {
    if (busy) return;
    if (code.trim().length !== 4) { setError('Codes are 4 letters.'); return; }
    setBusy('join'); setError(null);
    try { onEnterLobby(await joinLobby(code)); }
    catch (e) { setError(REASONS[(e as ApiError).code] ?? 'Could not join.'); setBusy(null); }
  }

  const lanternBalance = stats?.lanterns ?? character.lanterns ?? 0;
  const emberBalance = stats?.embers ?? character.embers ?? 0;
  const accountLevel = stats?.accountLevel ?? 1;
  const dailyDoneCount = (stats?.daily?.challenges ?? []).filter((c) => c.completed).length;
  const dailyTotal = (stats?.daily?.challenges ?? []).length;
  const featured = featuredGameForWeek();
  const featuredGame = games.find((g) => g.id === featured.gameId)
    ?? games.find((g) => (g.baseGameId ?? g.id) === featured.gameId);
  const featuredXpBoost = Math.round(FEATURED_XP_MULTIPLIER * 100 - 100);

  return (
    <main className="home-screen">
      {/* PIN banner — slim ribbon above the grid. */}

      {/* Mobile compact header — only visible on <700px. */}
      <header className="home-compact-header" aria-label="player summary">
        <button
          type="button"
          className="home-compact-me"
          onClick={onOpenLocker}
          aria-label="open locker"
        >
          <Character3DThumbnail icon={character.icon} size={36} alt={character.name} />
          <div className="home-compact-me-text">
            <span className="home-compact-name">{character.name}</span>
            <span className="muted home-compact-level">Lv {accountLevel}</span>
          </div>
        </button>
        <div className="home-compact-chips" aria-hidden="false">
          <span className="home-currency-chip" title="Lanterns">
            <span aria-hidden="true">🏮</span> {lanternBalance.toLocaleString()}
          </span>
          <span className="home-currency-chip" title="Embers">
            <span aria-hidden="true">🔥</span> {emberBalance.toLocaleString()}
          </span>
        </div>
        <div className="home-compact-icons">
          <button
            type="button"
            className="icon-btn"
            onClick={() => setMeOpen(true)}
            aria-label="settings"
            title="Settings"
          >
            <SettingsIcon size={18} />
          </button>
          <BellButton charId={character.charId} />
          <button
            type="button"
            className="icon-btn"
            onClick={() => setIdentityMenuOpen((v) => !v)}
            aria-label="more options"
            aria-haspopup="menu"
            aria-expanded={identityMenuOpen}
            title="More"
          >
            <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>⋯</span>
          </button>
        </div>
        {identityMenuOpen && (
          <div className="home-compact-menu" role="menu">
            <button type="button" role="menuitem" onClick={() => { setIdentityMenuOpen(false); onOpenLocker(); }}>Locker</button>
            <button type="button" role="menuitem" onClick={() => { setIdentityMenuOpen(false); onSwap(); }}>Swap character</button>
            <button type="button" role="menuitem" onClick={() => { setIdentityMenuOpen(false); { track('shop_open', { from: 'home' }); setBuyLanternsOpen(true); }; }}>Buy Lanterns</button>
            {character.hasPin === false && (
              <button type="button" role="menuitem" onClick={() => { setIdentityMenuOpen(false); setPinError(null); setPinOpen(true); }}>Set PIN</button>
            )}
          </div>
        )}
      </header>

      {/* Identity (PC + Mid). */}
      <section className="home-identity-card" aria-label="character">
        <div
          className={[
            'home-identity-seat',
            'seat',
            'presence-preview',
            character.seatCosmetics?.border ? `seat-border-${character.seatCosmetics.border}` : 'seat-border-plain',
            character.seatCosmetics?.background ? `seat-bg-${character.seatCosmetics.background}` : 'seat-bg-plain',
            character.seatCosmetics?.particle ? `seat-particle-${character.seatCosmetics.particle}` : 'seat-particle-none',
          ].join(' ')}
          {...(character.seatCosmetics?.bgHue !== null && character.seatCosmetics?.bgHue !== undefined
            ? { 'data-bg-hue': String(character.seatCosmetics.bgHue) }
            : {})}
          style={character.seatCosmetics?.bgHue !== null && character.seatCosmetics?.bgHue !== undefined
            ? ({ ['--seat-bg-h' as string]: String(character.seatCosmetics.bgHue) } as React.CSSProperties)
            : undefined}
        >
          <span className="seat-lamp" aria-hidden="true" />
          {/* LIVE animated hero (spinning turntable + showcase backdrop +
              particles + aura), exactly like the locker — replaces the old
              cached static PNG thumbnail so the main-menu identity actually
              MOVES and shows the player's flair. Bounded to this ONE tile (1
              WebGL context; hidden entirely on mobile, where the compact header
              keeps the cheap cached still). Drag-to-rotate on, like the locker.
              Layered fallback → cached still on chunk-load failure. */}
          <LiveCharacterAvatar icon={character.icon} size={216} interactive alt={character.name} />
        </div>
        <div className="home-identity-name">{character.name}</div>
        <div className="muted home-identity-sub">
          Lv {accountLevel}
          {typeof stats?.totalPoints === 'number' && (
            <> · {stats.totalPoints.toLocaleString()} pts</>
          )}
        </div>
        <ProfileFlair stats={stats} games={games} />
        <IdentityStatStack stats={stats} />
        <RecentlyUnlocked stats={stats} />
        <div className="home-identity-actions">
          <button
            type="button"
            className="icon-btn home-identity-icon"
            onClick={onOpenLocker}
            aria-label="open locker"
            title="Customize in Locker"
          >
            <Backpack size={16} />
          </button>
          <button
            type="button"
            className="icon-btn home-identity-icon"
            onClick={onSwap}
            aria-label="swap character"
            title="Swap character"
          >
            <RepeatIcon size={16} />
          </button>
          <button
            type="button"
            className="icon-btn home-identity-icon"
            onClick={() => setMeOpen(true)}
            aria-label="settings"
            title="Settings"
          >
            <SettingsIcon size={16} />
          </button>
          {character.hasPin === false && (
            <button
              type="button"
              className="link"
              style={{ color: 'var(--accent-gold, #ffcd6b)' }}
              onClick={() => { setPinError(null); setPinOpen(true); }}
            >
              set pin
            </button>
          )}
        </div>
      </section>

      {/* Hero — the headline + primary CTA + JOIN code. */}
      <section className="home-hero" aria-label="open a table">
        <h1 className="display home-title">The tables are open.</h1>
        <p className="muted home-hero-sub">Pull up a chair, or set one for friends.</p>

        <button
          type="button"
          className={`envelope-btn${busy === 'create' ? ' is-busy' : ''}`}
          onClick={() => { if (!busy) setHostModalOpen(true); }}
          disabled={busy !== null}
          aria-haspopup="dialog"
        >
          <span className="envelope-flap" aria-hidden="true" />
          <span className="envelope-seal" aria-hidden="true">T</span>
          <span className="envelope-label">{busy === 'create' ? 'Setting the table…' : 'Open a Table'}</span>
          <span className="envelope-sub">{busy === 'create' ? 'sealing the invite…' : 'host or join'}</span>
        </button>

        <form className="knocker-form" onSubmit={(e) => { e.preventDefault(); void join(); }} noValidate>
          <div className="knocker-slots" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className={`knocker-slot${code.length === i ? ' is-active' : ''}${code[i] ? ' is-filled' : ''}`}>
                {code[i] ?? '·'}
              </span>
            ))}
          </div>
          <input
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={4}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4))}
            placeholder="ABCD"
            className="knocker-hidden-input"
            aria-label="room code"
          />
          <button type="submit" className="knocker-btn" disabled={busy !== null || code.length !== 4} aria-label="join with code">
            <svg viewBox="0 0 56 56" aria-hidden="true">
              <defs>
                <linearGradient id="knocker-brass" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#f5c14a" />
                  <stop offset="0.5" stopColor="#a87a3a" />
                  <stop offset="1" stopColor="#5a3818" />
                </linearGradient>
              </defs>
              <circle cx="28" cy="28" r="26" fill="url(#knocker-brass)" stroke="#1a0e08" strokeWidth="2" />
              <ellipse cx="28" cy="22" rx="14" ry="12" fill="none" stroke="#1a0e08" strokeWidth="3.5" />
              <circle cx="28" cy="40" r="4" fill="#3a2410" />
            </svg>
          </button>
        </form>
        <div className="knocker-caption muted">
          {busy === 'join' ? 'knocking…' : 'knock on a door — enter the 4-letter code'}
        </div>

        <div className="home-hero-browse">
          <button
            type="button"
            className="btn ghost"
            onClick={() => setBrowseOpen(true)}
            style={{ fontSize: 13 }}
          >
            Browse open tables
          </button>
        </div>

        {error && <div role="alert" className="gate-error">{error}</div>}
      </section>

      {/* Notifs + currency + utility column (PC + Mid). */}
      <aside className="home-notifs-col" aria-label="notifications and currency">
        <div className="home-notifs-card">
          <div className="home-currency-row">
            <span className="home-currency-chip" title="Lanterns">
              <span aria-hidden="true">🏮</span> {lanternBalance.toLocaleString()}
            </span>
            <span className="home-currency-chip" title="Embers">
              <span aria-hidden="true">🔥</span> {emberBalance.toLocaleString()}
            </span>
          </div>
          <div className="home-level-mini">
            <XPBar charId={character.charId} />
          </div>
          <div className="home-notifs-bell-row">
            <BellButton charId={character.charId} />
            <button
              type="button"
              className="btn ghost"
              style={{ fontSize: 12, padding: '4px 10px' }}
              onClick={() => { track('shop_open', { from: 'home' }); setBuyLanternsOpen(true); }}
              aria-label="Buy lanterns"
            >
              Buy Lanterns
            </button>
          </div>
        </div>
        <div className="home-utility-row">
          <button
            type="button"
            className="icon-btn home-utility-icon"
            onClick={() => setMeOpen(true)}
            aria-label="settings"
            title="Settings"
          >
            <SettingsIcon size={14} />
          </button>
          <button
            type="button"
            className="icon-btn home-utility-icon"
            onClick={() => window.open('https://tile-tavern.example/help', '_blank', 'noopener')}
            aria-label="help"
            title="Help"
          >
            <HelpCircle size={14} />
          </button>
        </div>
      </aside>

      <RegularsRail character={character} onEnterLobby={onEnterLobby} />

      {/* Doorway tiles — span all columns. */}
      <nav className="home-doorways" aria-label="more places to go">
        <DoorwayTile label="Locker" onClick={onOpenLocker} icon={<Backpack size={22} />} />
        <DoorwayTile label="My Tavern" onClick={onOpenTavern} icon={<HomeIcon size={22} />} />
        <DoorwayTile
          label="Season Pass"
          onClick={() => {
            if (showSeasonNag) dismissSeasonNag();
            onOpenSeason();
          }}
          icon={<Ticket size={22} />}
          highlight={showSeasonNag}
          badge={showSeasonNag ? (seasonUnclaimedCount > 9 ? '9+' : String(seasonUnclaimedCount)) : null}
          ariaLabel={showSeasonNag
            ? `Season Pass — ${seasonUnclaimedCount} reward${seasonUnclaimedCount === 1 ? '' : 's'} waiting`
            : 'Season Pass'}
        />
        <DoorwayTile
          label="Lantern Shop"
          onClick={onOpenShop}
          icon={<span aria-hidden="true" style={{ fontSize: 22, lineHeight: 1 }}>🏮</span>}
        />
        <DoorwayTile label="Achievements" onClick={onOpenAchievements} icon={<Trophy size={22} />} />
        <DoorwayTile label="Hall of Fame" onClick={onOpenHof} icon={<Crown size={22} />} />
      </nav>

      {/* Featured (PC/Mid). On mobile this is collapsed at the bottom. */}
      <section className="home-featured-card" aria-label="tonight's featured">
        <div className="home-featured-head">
          <Sparkles size={14} aria-hidden="true" />
          <span>Tonight's Featured</span>
        </div>
        {featuredGame ? (
          <>
            <div className="home-featured-body">
              <GameThumb gameId={featuredGame.id} category={featuredGame.category} name={featuredGame.name} size={88} />
              <div className="home-featured-text">
                <div className="home-featured-name">{featuredGame.name}</div>
                <div className="home-featured-pitch muted">{featuredGame.pitch ?? prettifyGameId(featured.gameId)}</div>
                <div className="home-featured-meta">
                  {featuredGame.category && <span className="meta-pill">{featuredGame.category}</span>}
                  {typeof featuredGame.durationSec === 'number' && featuredGame.durationSec > 0 && (
                    <span className="meta-pill">{featuredGame.durationSec}s</span>
                  )}
                </div>
                <div className="home-featured-boost">+{featuredXpBoost}% XP today</div>
              </div>
            </div>
            <button
              type="button"
              className="btn primary home-featured-cta"
              onClick={() => { if (!busy) setHostModalOpen(true); }}
              disabled={busy !== null}
            >
              Play this game
            </button>
          </>
        ) : (
          <div className="home-featured-body">
            <div className="home-featured-text">
              <div className="home-featured-name">{prettifyGameId(featured.gameId)}</div>
              <div className="home-featured-boost">+{featuredXpBoost}% XP today</div>
            </div>
          </div>
        )}
      </section>

      {/* Dailies inline (PC/Mid). On mobile this collapses below. */}
      <section className="home-dailies-card" aria-label="daily challenges">
        <div className="home-dailies-head">
          <Target size={14} aria-hidden="true" />
          <span>Daily Challenges</span>
          {dailyTotal > 0 && (
            <span className="muted home-dailies-count">{dailyDoneCount} / {dailyTotal} done today</span>
          )}
        </div>
        <div className="home-dailies-body">
          <DailyChallengeWidget charId={character.charId} embedded />
          {dailyTotal > 0 && dailyTotal < 3 && (
            <div className="muted home-dailies-hint">Come back tomorrow for more.</div>
          )}
          <DailiesRewardFooter stats={stats} />
        </div>
      </section>

      {/* Mobile-only collapsibles, mirroring Featured + Dailies. */}
      <div className="home-mobile-collapsibles">
        <CollapseSection
          id="daily-mobile"
          title="Daily challenges"
          icon={<Target size={16} />}
          summary={dailyTotal > 0 ? `${dailyDoneCount} / ${dailyTotal} done today` : undefined}
          defaultOpen={false}
        >
          <DailyChallengeWidget charId={character.charId} />
        </CollapseSection>
        <CollapseSection
          id="featured-mobile"
          title="Tonight's featured"
          icon={<Sparkles size={16} />}
          summary={featuredGame?.name ?? prettifyGameId(featured.gameId)}
          defaultOpen={false}
        >
          {featuredGame ? (
            <div className="home-featured-body">
              <GameThumb gameId={featuredGame.id} category={featuredGame.category} name={featuredGame.name} size={72} />
              <div className="home-featured-text">
                <div className="home-featured-name">{featuredGame.name}</div>
                <div className="home-featured-pitch muted">{featuredGame.pitch ?? ''}</div>
                <div className="home-featured-boost">+{featuredXpBoost}% XP today</div>
              </div>
            </div>
          ) : (
            <div className="muted">+{featuredXpBoost}% XP today</div>
          )}
        </CollapseSection>
      </div>

      {/* Modals + drawers. */}
      {hostModalOpen && (
        <HostVisibilityModal
          defaultVisibility={visibility}
          onCancel={() => setHostModalOpen(false)}
          onChoose={(v) => void create(v)}
        />
      )}
      {browseOpen && (
        <PublicBrowser
          games={games}
          onJoined={(t) => { setBrowseOpen(false); onEnterLobby(t); }}
          onClose={() => setBrowseOpen(false)}
        />
      )}
      {buyLanternsOpen && (
        <BuyLanternsModal onClose={() => {
          setBuyLanternsOpen(false);
          void onCharacterRefresh?.();
        }} />
      )}
      <ToastQueue toasts={homeToasts.toasts} onDismiss={homeToasts.dismiss} />
      {pinOpen && (
        <PinPad
          mode="set"
          title={pinMandatory ? 'Lock your character to continue' : 'Set a 4-digit PIN'}
          subtitle={`${character.name} — locks this character to your devices`}
          error={pinError}
          busy={pinBusy}
          onCancel={() => { if (!pinBusy && !pinMandatory) setPinOpen(false); }}
          {...(pinMandatory ? { mandatory: true } : {})}
          onSubmit={(pin) => {
            setPinBusy(true);
            setPinError(null);
            void (async () => {
              try {
                await setCharacterPin(character.charId, pin);
                onCharacterUpdated({ ...character, hasPin: true });
                setPinOpen(false);
              } catch (e) {
                if (e instanceof ApiError && e.code === 'pin-already-set') {
                  setPinError('A PIN is already set on this character.');
                } else {
                  setPinError('Could not set PIN. Try again.');
                }
              } finally {
                setPinBusy(false);
              }
            })();
          }}
        />
      )}
      <MeDrawer open={meOpen} onClose={() => setMeOpen(false)} />
    </main>
  );
}

/**
 * "Your Own Tavern" Phase D2 — regulars rail. Polls GET /regulars every
 * 30s while Home is mounted (plus a refetch on window focus) so live
 * "at a table" status stays current without a hard reload. Hidden
 * entirely for a fresh account (no regulars added AND nobody's added
 * them) — zero clutter for new players.
 */
function RegularsRail({ character, onEnterLobby }: { character: Character; onEnterLobby: (t: LobbyTicket) => void }) {
  const [regulars, setRegulars] = useState<RegularEntry[]>([]);
  const [reverseCount, setReverseCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [joinBusyCode, setJoinBusyCode] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    async function load() {
      try {
        const res = await getRegulars(character.charId);
        if (!live) return;
        setRegulars(res.regulars);
        setReverseCount(res.reverseCount);
      } catch { /* rail is decorative — silent on failure */ }
      finally { if (live) setLoaded(true); }
    }
    void load();
    const interval = window.setInterval(() => void load(), 30000);
    function onFocus() { void load(); }
    window.addEventListener('focus', onFocus);
    return () => {
      live = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [character.charId]);

  async function join(roomCode: string) {
    if (joinBusyCode) return;
    setJoinBusyCode(roomCode);
    setJoinError(null);
    try {
      onEnterLobby(await joinLobby(roomCode));
    } catch (e) {
      setJoinError(REASONS[(e as ApiError).code] ?? 'Could not join that table.');
      setJoinBusyCode(null);
    }
  }

  if (!loaded || (regulars.length === 0 && reverseCount === 0)) return null;

  return (
    <section className="home-regulars-rail" aria-label="your regulars">
      <div className="home-regulars-head">
        <span>Your Regulars</span>
        {reverseCount > 0 && (
          <span className="muted home-regulars-hint">+{reverseCount} count you as a regular</span>
        )}
      </div>
      {regulars.length > 0 ? (
        <div className="home-regulars-scroll">
          {regulars.map((r) => {
            const atTable = r.status?.kind === 'at-table' ? r.status : null;
            return (
              <div key={r.charId} className={`home-regular-card${atTable ? ' is-live' : ''}`}>
                <Character3DThumbnail icon={r.icon} size={40} alt={r.name} />
                <div className="home-regular-card-text">
                  <span className="home-regular-name">
                    {r.name}
                    {r.mutual && <span className="home-regular-mutual-badge" title="Regulars — you've both added each other" aria-label="mutual regular">☕</span>}
                  </span>
                  {atTable ? (
                    atTable.joinable && atTable.roomCode ? (
                      <button
                        type="button"
                        className="home-regular-join"
                        onClick={() => void join(atTable.roomCode!)}
                        disabled={joinBusyCode === atTable.roomCode}
                      >
                        At {r.name}'s table — {atTable.size}/{atTable.cap} · {joinBusyCode === atTable.roomCode ? 'joining…' : 'JOIN'}
                      </button>
                    ) : (
                      <span className="muted home-regular-status">At a table — {atTable.size}/{atTable.cap}</span>
                    )
                  ) : (
                    <span className="muted home-regular-status is-idle">In the tavern… sometime</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="muted home-regulars-empty">Add a regular from their profile to see them here.</div>
      )}
      {joinError && <div role="alert" className="gate-error" style={{ margin: 0 }}>{joinError}</div>}
    </section>
  );
}

interface DoorwayTileProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /** Optional badge text (e.g. "3" or "9+"). */
  badge?: string | null;
  /** When true, paints the tile in nag/highlight gold. */
  highlight?: boolean;
  /** Override aria-label (defaults to label). */
  ariaLabel?: string;
}

function DoorwayTile({ label, icon, onClick, badge, highlight, ariaLabel }: DoorwayTileProps) {
  return (
    <button
      type="button"
      className={`home-doorway${highlight ? ' has-nag' : ''}`}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
    >
      <span className="home-doorway-frame">
        {icon}
        {badge && (
          <span className="home-doorway-badge" aria-hidden="true">{badge}</span>
        )}
      </span>
      <span className="home-doorway-sign">{label}</span>
    </button>
  );
}

interface HostVisibilityProps {
  defaultVisibility: 'public' | 'private';
  onCancel: () => void;
  onChoose: (v: 'public' | 'private') => void;
}

function HostVisibilityModal({ defaultVisibility, onCancel, onChoose }: HostVisibilityProps) {
  useEscapeClose(true, onCancel);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Open a Table — choose visibility"
      className="host-visibility-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="host-visibility-modal">
        <header className="host-visibility-head">
          <h2 className="display">Open a Table</h2>
          <button type="button" className="icon-btn" onClick={onCancel} aria-label="Close">
            <X size={18} />
          </button>
        </header>
        <p className="muted host-visibility-sub">Who can sit at this table?</p>
        <div className="host-visibility-choices">
          <button
            type="button"
            className={`host-visibility-choice${defaultVisibility === 'private' ? ' is-default' : ''}`}
            onClick={() => onChoose('private')}
          >
            <span className="host-visibility-glyph" aria-hidden="true">🔒</span>
            <span className="host-visibility-label">Host Private</span>
            <span className="host-visibility-desc muted">Code only — share the 4-letter code with friends.</span>
          </button>
          <button
            type="button"
            className={`host-visibility-choice${defaultVisibility === 'public' ? ' is-default' : ''}`}
            onClick={() => onChoose('public')}
          >
            <span className="host-visibility-glyph" aria-hidden="true">🌍</span>
            <span className="host-visibility-label">Host Public</span>
            <span className="host-visibility-desc muted">Anyone can join — your table appears in Browse.</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileFlair({ stats, games }: { stats: CharacterStats | null; games: GameTile[] }) {
  if (!stats) return <div className="profile-flair-row"><span className="flair-chip">Fresh face</span></div>;
  const perGame = Object.entries(stats.perGame);
  const wins = perGame.reduce((sum, [, g]) => sum + (g.wins ?? 0), 0);
  const matches = perGame.reduce((sum, [, g]) => sum + (g.matchesPlayed ?? 0), 0);
  const favorite = stats.favoriteGameId ? [stats.favoriteGameId] as const : perGame.sort((a, b) => (b[1].matchesPlayed ?? 0) - (a[1].matchesPlayed ?? 0))[0];
  const favoriteName = favorite ? (games.find((g) => g.id === favorite[0]) ?? games.find((g) => g.baseGameId === favorite[0]))?.name ?? favorite[0] : null;
  const title = stats.selectedTitle ?? (wins >= 10 ? 'Table regular' : wins > 0 ? 'Round winner' : matches > 0 ? 'Rising regular' : 'Fresh face');

  return (
    <div className="profile-flair-row" aria-label="profile flair">
      <span className="flair-chip">{title}</span>
      <span className="flair-chip">{wins} wins</span>
      {favoriteName && <span className="flair-chip">Fav: {favoriteName}</span>}
      {stats.achievements.length > 0 && <span className="flair-chip">{stats.achievements.length} badges</span>}
    </div>
  );
}

/** Extra stats stack on the identity card — daily streak + set wins.
 *  Each chip self-suppresses when its underlying value is missing or 0
 *  so the card collapses gracefully for fresh accounts. */
function IdentityStatStack({ stats }: { stats: CharacterStats | null }) {
  if (!stats) return null;
  const streak = typeof stats.dailyStreak === 'number' ? stats.dailyStreak : 0;
  const setWins = typeof stats.setWins === 'number' ? stats.setWins : 0;
  if (streak < 1 && setWins < 1) return null;
  return (
    <div className="profile-flair-row" aria-label="player streak and set wins">
      {streak >= 1 && (
        <span className="flair-chip" title="Daily login streak">
          <FlameIcon size={12} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 2 }} />
          {streak} day streak
        </span>
      )}
      {setWins >= 1 && (
        <span className="flair-chip" title="Set wins">
          <Crown size={12} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 2 }} />
          {setWins} set wins
        </span>
      )}
    </div>
  );
}

/** Last unlocked cosmetic — picks the final entry of the unlockHints
 *  list filtered to `.unlocked === true`. The server emits unlock hints
 *  without a `claimedAt` timestamp, so "last" means "last in array
 *  order"; if the server later sorts by claim order this still does the
 *  right thing. Returns null cleanly when nothing has been unlocked. */
function RecentlyUnlocked({ stats }: { stats: CharacterStats | null }) {
  if (!stats) return null;
  const unlocked = (stats.inventory.unlockHints ?? []).filter((h) => h.unlocked);
  if (unlocked.length === 0) return null;
  const last = unlocked[unlocked.length - 1]!;
  // Map unlock-hint `kind` → cosmetic-resolver kind (camelCase → kebab).
  const resolverKind = (() => {
    switch (last.kind) {
      case 'seatBorder': return 'seat-border' as const;
      case 'seatBackground': return 'seat-background' as const;
      case 'seatParticle': return 'seat-particle' as const;
      case 'seatBgHue': return 'seat-bghue' as const;
      case 'accentHue': return 'accent' as const;
      case 'emote': return null;
      case 'body':
      case 'hat':
      case 'face':
      case 'palette':
      case 'border':
      case 'background':
      case 'mask':
      case 'title':
        return last.kind;
      default:
        return null;
    }
  })();
  const catId = resolverKind ? inventoryToCatalogId(resolverKind, last.id) : null;
  return (
    <div className="home-identity-recent" aria-label="recently unlocked">
      <div className="home-identity-recent-text">
        <span className="muted home-identity-recent-label">Recently unlocked</span>
        <span className="home-identity-recent-name">{last.label}</span>
      </div>
      {catId && (
        <div className="home-identity-recent-thumb" aria-hidden="true">
          <CosmeticThumbnail3D cosmeticId={catId} size={36} />
        </div>
      )}
    </div>
  );
}

/** Today's-reward-total footer for the dailies card. Sums season XP and
 *  embers across all three challenges. Skips entirely if no challenges
 *  are loaded (the widget itself returns null in that case anyway). */
function DailiesRewardFooter({ stats }: { stats: CharacterStats | null }) {
  const challenges = stats?.daily?.challenges ?? [];
  if (challenges.length === 0) return null;
  const totalSxp = challenges.reduce((sum, c) => sum + (c.rewardSeasonXp ?? 0), 0);
  const totalEmbers = challenges.reduce((sum, c) => sum + (c.rewardEmbers ?? 0), 0);
  if (totalSxp === 0 && totalEmbers === 0) return null;
  return (
    <div className="home-dailies-foot muted">
      Today's reward total:
      {totalSxp > 0 && <> +{totalSxp} SXP</>}
      {totalSxp > 0 && totalEmbers > 0 && <> · </>}
      {totalEmbers > 0 && <> +{totalEmbers} 🔥</>}
      {' if you complete all 3.'}
    </div>
  );
}
